import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Tag } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(false));
    api.get("/blog").then((r) => setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (post === null) return <div className="min-h-[60vh] flex items-center justify-center">Loading…</div>;
  if (post === false) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <h2 className="font-display text-2xl text-[#101827] mb-3">Article not found</h2>
        <Link to="/blog" className="ae-btn-primary"><ArrowLeft className="w-4 h-4" /> Back to Knowledge</Link>
      </div>
    </div>
  );

  // very simple markdown-ish rendering
  const html = post.content
    .split("\n")
    .map((line) => {
      if (line.startsWith("## ")) return `<h2 class="font-display text-2xl font-semibold text-[#101827] mt-10 mb-3">${line.slice(3)}</h2>`;
      if (line.startsWith("- ")) return `<li class="mb-1">${line.slice(2)}</li>`;
      if (/^\d+\.\s/.test(line)) return `<li class="mb-1">${line.replace(/^\d+\.\s/, "")}</li>`;
      if (line.startsWith("**")) return `<p class="mt-4"><strong>${line.replaceAll("**", "")}</strong></p>`;
      if (line.trim() === "") return "<br/>";
      return `<p class="mt-3 leading-relaxed text-[#1F2937]">${line}</p>`;
    })
    .join("");

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.cover_image}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          image: post.cover_image,
          author: { "@type": "Organization", name: post.author },
          publisher: { "@type": "Organization", name: "Apurba Estate" },
          datePublished: post.created_at,
          dateModified: post.updated_at,
        }}
      />
      <section className="bg-[#101827] text-white">
        <div className="ae-container pt-24 pb-14 max-w-4xl">
          <Link to="/blog" className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1 mb-6"><ArrowLeft className="w-4 h-4" /> All articles</Link>
          <span className="ae-chip mb-5"><Tag className="w-3 h-3" /> {post.category}</span>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.1]">{post.title}</h1>
          <div className="mt-5 text-sm text-white/60 flex items-center gap-3"><Calendar className="w-4 h-4" /> {post.author}</div>
        </div>
      </section>

      {post.cover_image && (
        <div className="ae-container -mt-10 relative z-10">
          <div className="ae-img-elevated aspect-[16/8]">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      <section className="ae-section">
        <div className="ae-container max-w-3xl prose-ae" dangerouslySetInnerHTML={{ __html: html }} />
      </section>

      {related.length > 0 && (
        <section className="ae-section bg-[#F4F6F8]">
          <div className="ae-container">
            <h3 className="font-display text-2xl font-semibold text-[#101827] mb-6">Related articles</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="ae-card overflow-hidden">
                  <div className="aspect-[4/3] bg-white"><img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" /></div>
                  <div className="p-5">
                    <span className="ae-chip mb-3">{p.category}</span>
                    <h4 className="font-bold text-[#101827] text-base line-clamp-2">{p.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
